import React, { useState, useEffect, useCallback } from 'react';
import ListLoadErrorBanner from '../../components/ui/ListLoadErrorBanner';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import siteService, { Site } from '../../services/siteService';
import companyService from '../../services/companyService';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { CanCreate } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import {
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiMapPinLine,
  RiFilterLine,
  RiFilterOffLine,
  RiDeleteBin2Line,
  RiFileDownloadLine,
  RiSearchLine,
  RiAddLine,
  RiRefreshLine,
  RiCloseLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiMapPin2Fill,
  RiPhoneLine,
  RiUserLine,
  RiBuilding2Line,
} from 'react-icons/ri';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { useDeleteSite } from '../../hooks/mutations/useSiteMutations';
import { queryKeys } from '../../queryClient';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { getLocalDateString } from '../../utils/dateUtils';
import { formatActivationCode } from '../../utils/activationCode';
import { downloadExcelFile } from '../../utils/downloadExcel';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';
import { useDropUnknownCompanyFilter } from '../../hooks/useDropUnknownCompanyFilter';
import { detailNavState } from '../../components/detail';
import { SITE_NEW_URL, siteDetailUrl, siteEditUrl } from '../../utils/siteLinks';

const SitePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission();
  // S1111 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlCompanyParam = searchParams.get('company') || '';
  const urlStatusParam = searchParams.get('status') || '';
  // KD3 S2482 (REGR-MOVE-008): คำค้นอยู่ใน URL (?q=) → ออกไปหน้าอื่นแล้วกด Back คำค้น+ผลลัพธ์ไม่หาย
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  // FINDING-S980-A FIX (refactored S1035): consume itemsPerPage via shared hook
  useUserPageSize(pageSize, setPageSize);

  // State สำหรับ filter - ย้ายขึ้นมาก่อน React Query
  // S1111 URL-sync: hydrate filters from URL
  const [filterCompany, setFilterCompany] = useState<string>(urlCompanyParam);
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1111 URL-sync: write filter+page to URL when state changes (Variant A overwrite — no other URL params on this page)
  const syncFiltersToUrl = useCallback((company: string, status: string, page: number, search: string) => {
    const newParams = new URLSearchParams();
    if (company) newParams.set('company', company);
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (search) newParams.set('q', search);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterCompany, filterStatus, currentPage, searchTerm);
  }, [filterCompany, filterStatus, currentPage, searchTerm, syncFiltersToUrl]);

  // ลิงก์เก่า `?view=<site_id>` (การแจ้งเตือน site_idle เก่า / bookmark) → LegacyDetailRedirect ใน App.tsx
  // เด้งไปหน้ารายละเอียดก่อนหน้านี้จะ mount (28 ต.ค. 2026 — เดิมเปิดโมดัลจาก effect ตรงนี้)

  const deleteSiteMutation = useDeleteSite();

  // React Query สำหรับดึงข้อมูล
  const { data: sitesData, isLoading: sitesLoading, error: sitesError, refetch } = useQuery({
    queryKey: [...queryKeys.sites.list(), { page: currentPage, search: searchTerm, limit: pageSize, company_id: filterCompany, status: filterStatus }],
    queryFn: async () => await siteService.getSites({
      page: currentPage,
      search: searchTerm,
      limit: pageSize,
      company_id: filterCompany ? Number(filterCompany) : undefined,
      status: filterStatus || undefined
    }),
  });

  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: queryKeys.companies.list(),
    queryFn: async () => await companyService.getCompanies({
      limit: PAGINATION_CONFIG.DROPDOWN_LIMIT_LARGE, // ดึงข้อมูลทั้งหมดสำหรับ dropdown (companies endpoint รับ max 1000)
      page: 1
    }),
  });
  useDropUnknownCompanyFilter(companiesData?.items, companiesData?.total, filterCompany, () => setFilterCompany(''));

  // ข้อมูลจาก React Query
  const sites = sitesData?.items || [];
  const companies = companiesData?.items || [];
  const totalItems = sitesData?.total || 0;
  useClampPage(currentPage, sitesData?.total, pageSize, setCurrentPage, sitesLoading);
  const loading = sitesLoading || companiesLoading || deleteSiteMutation.isPending;

  const [selectedSites, setSelectedSites] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  // ดู/เพิ่ม/แก้ไข = หน้าเต็ม (มติ PO 28 ต.ค. 2026 — เดิมโมดัล) · แนบ URL รายการ (ตัวกรอง/หน้า/คำค้น) ไปเป็น from
  // ให้ปุ่ม "กลับ" / หลังบันทึก กลับมาที่เดิม
  const handleAddSite = () => navigate(SITE_NEW_URL, { state: detailNavState(location) });
  const handleViewSite = (site: Site) => navigate(siteDetailUrl(site.id), { state: detailNavState(location) });
  const handleEditSite = (site: Site) => navigate(siteEditUrl(site.id), { state: detailNavState(location) });

  // ฟังก์ชันสำหรับลบโครงการ
  const handleDeleteSite = (site: Site) => {
    showConfirm(
      'ยืนยันการลบโครงการ',
      `คุณต้องการลบโครงการ ${site.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`,
      async () => {
        deleteSiteMutation.mutate(site.id);
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับการเลือกโครงการทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedSites([]);
  }, [currentPage, pageSize, searchTerm, filterCompany, filterStatus]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedSites(sites.map(site => site.id));
    } else {
      setSelectedSites([]);
    }
  };

  // ฟังก์ชันสำหรับการเลือกโครงการแต่ละรายการ
  const handleSelectSite = (siteId: number, selected: boolean) => {
    if (selected) {
      setSelectedSites(prev => [...prev, siteId]);
    } else {
      setSelectedSites(prev => prev.filter(id => id !== siteId));
    }
  };

  // ฟังก์ชันสำหรับการลบโครงการหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedSites.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกโครงการที่ต้องการลบ', 'info');
      return;
    }

    showConfirm(
      'ยืนยันการลบโครงการ',
      `คุณต้องการลบโครงการ ${selectedSites.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedSites, id => siteService.deleteSite(id));
          setSelectedSites(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบโครงการ ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('โครงการ', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // คอลัมน์ของตาราง - Enhanced Design
  const columns = [
    ...(canDelete('sites') ? [{
      key: 'select',
      title: (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            aria-label="เลือกโครงการทั้งหมด"
            checked={selectedSites.length === sites.length && sites.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 transition-colors cursor-pointer"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: Site) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            aria-label={`เลือกโครงการ ${record.name || record.code || record.id}`}
            checked={selectedSites.includes(record.id)}
            onChange={(e) => handleSelectSite(record.id, e.target.checked)}
            className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 transition-colors cursor-pointer"
          />
        </div>
      ),
    }] : []),
    {
      key: 'code',
      title: 'รหัสโครงการ',
      sortable: true,
      // feedback user 3 ก.ย. 2026: ค้นด้วยรหัส Activate ได้แล้ว (backend) — โชว์รหัสใต้แถวด้วย
      // ให้เห็นว่าผลลัพธ์ตรงกับที่พิมพ์ค้นหา (เดิมเห็นแต่รหัสโครงการ SITExxxx)
      render: (value: string, record: Site) => {
        const activation = formatActivationCode(record.activation_code);
        const customerNo = formatActivationCode(record.customer_number || record.customer_code);
        return (
          <div className="flex flex-col gap-1 items-start">
            <span className="font-mono text-sm font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 px-3 py-1.5 rounded-lg border border-primary-200/50 shadow-sm">
              {value || '-'}
            </span>
            {(activation || customerNo) && (
              <span className="text-xs text-secondary-500 font-mono">
                {activation && <span title="รหัส Activate">Activate: {activation}</span>}
                {activation && customerNo && ' · '}
                {customerNo && <span title="รหัสลูกค้า">ลูกค้า: {customerNo}</span>}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'name',
      title: 'ชื่อโครงการ',
      sortable: true,
      render: (value: string, record: Site) => (
        <div className="group">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
              <RiMapPin2Fill className="w-4 h-4 text-white" />
            </div>
            {/* KD3 S2492 (NEG-MD-034): truncate ไม่ทำงานในเซลล์ตารางที่ไม่มีความกว้างจำกัด — ชื่อใช้งานจริง 500 ตัว
                ดันคอลัมน์กว้าง 3,734px จนบริษัท/สถานะหลุดจอ · จำกัดความกว้าง + title ชื่อเต็ม */}
            <div className="min-w-0 max-w-[22rem]">
              <p className="font-medium text-secondary-900 truncate group-hover:text-primary-600 transition-colors" title={value}>
                {value}
              </p>
              {record.display_name && record.display_name !== value && (
                <p className="text-xs text-secondary-500 truncate mt-0.5" title={record.display_name}>
                  {record.display_name}
                </p>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'company',
      title: 'บริษัท',
      sortable: true,
      render: (value: any, record: Site) => (
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 w-7 h-7 bg-secondary-100 rounded-md flex items-center justify-center">
            <RiBuilding2Line className="w-3.5 h-3.5 text-secondary-500" />
          </div>
          <span className="text-secondary-700 font-medium truncate max-w-[16rem]" title={record.company?.name || undefined}>
            {record.company?.name || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'สถานะ',
      sortable: true,
      render: (value: string) => {
        const isActive = value === 'active';
        return (
          <div className={`
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
            transition-all duration-200 cursor-default
            ${isActive
              ? 'bg-gradient-to-r from-success-50 to-success-100 text-success-700 border border-success-200/50 shadow-sm shadow-success-100'
              : value === 'inactive'
              ? 'bg-gradient-to-r from-danger-50 to-danger-100 text-danger-700 border border-danger-200/50 shadow-sm shadow-danger-100'
              : 'bg-secondary-100 text-secondary-600 border border-secondary-200/50'
            }
          `}>
            {isActive ? (
              <RiCheckLine className="w-3.5 h-3.5" />
            ) : value === 'inactive' ? (
              <RiCloseCircleLine className="w-3.5 h-3.5" />
            ) : null}
            <span>
              {isActive
                ? 'ใช้งาน'
                : value === 'inactive'
                ? 'ไม่ใช้งาน'
                : value || 'ไม่ระบุ'}
            </span>
          </div>
        );
      },
    },
    {
      // Phase 2 สถานะโครงการ: จำนวนการเช่าที่ยังใช้งาน (aggregate จาก backend)
      // ช่วยตัดสินใจก่อนปิดโครงการ — เขียว = ยังเช่าอยู่, เทา "ว่าง" = ปิดได้
      // E2E v6 OBS-4c: โชว์ร่างการเช่า (DRAFT) ด้วย — ไม่นับเป็น "เช่าอยู่" แต่ระบบยังถือว่าโครงการใช้งานอยู่
      // (ไม่เสนอปิด / ปิดโครงการเดิมตอนย้ายอุปกรณ์ไม่ได้) → "ว่าง · ร่าง M" กันเข้าใจผิดว่าปิดได้ทันที
      key: 'active_rental_count',
      title: 'การเช่าใช้งาน',
      render: (_value: any, record: Site) => {
        const count = record.active_rental_count || 0;
        const drafts = record.draft_rental_count || 0;
        const draftTitle = drafts > 0
          ? `มีร่างการเช่า ${drafts} รายการ — ระบบยังถือว่าโครงการใช้งานอยู่`
          : undefined;
        if (count > 0) {
          return (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-success-50 to-success-100 text-success-700 border border-success-200/50 shadow-sm shadow-success-100"
              title={draftTitle}
            >
              เช่าอยู่ {count} รายการ{drafts > 0 ? ` · ร่าง ${drafts}` : ''}
            </span>
          );
        }
        if (drafts > 0) {
          return (
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-600 border border-secondary-200/50"
              title={draftTitle}
            >
              ว่าง · ร่าง {drafts}
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-600 border border-secondary-200/50">
            ว่าง
          </span>
        );
      },
    },
    {
      key: 'contactPerson',
      title: 'ผู้ติดต่อ',
      sortable: true,
      render: (_value: string, record: Site) => (
        <div className="space-y-1">
          {record.contactPerson ? (
            <>
              <div className="flex items-center gap-1.5">
                <RiUserLine className="w-3.5 h-3.5 text-secondary-400" />
                <span className="font-medium text-secondary-800">{record.contactPerson}</span>
              </div>
              {record.phone && (
                <div className="flex items-center gap-1.5">
                  <RiPhoneLine className="w-3.5 h-3.5 text-secondary-400" />
                  <span className="text-sm text-secondary-500 font-mono">{record.phone}</span>
                </div>
              )}
            </>
          ) : (
            <span className="text-secondary-500 text-sm italic">ไม่ระบุ</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'การจัดการ',
      sticky: true,
      render: (_: any, record: Site) => (
        <ActionDropdown
          actions={[
            ...(canView('sites') ? [{
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleViewSite(record)
            }] : []),
            ...(canEdit('sites') ? [{
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleEditSite(record)
            }] : []),
            ...(canDelete('sites') && (canView('sites') || canEdit('sites')) ? [{
              divider: true
            }] : []),
            ...(canDelete('sites') ? [{
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDeleteSite(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }] : [])
          ]}
          buttonIcon={null}
          buttonLabel=""
        />
      )
    }
  ];

  // ส่วนหัวของหน้า - Enhanced Design
  const renderPageHeader = () => {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiMapPinLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ข้อมูลโครงการ</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลโครงการทั้งหมดในระบบ</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>
    );
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชัน Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองที่กำลังใช้อยู่ไปด้วย — ไฟล์ต้องตรงกับที่เห็นบนจอ
      const blob = await siteService.exportToExcel({
        search: searchTerm || undefined,
        company_id: filterCompany || undefined,
        status: filterStatus || undefined,
      });
      downloadExcelFile(blob, `sites_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // ฟังก์ชัน Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilterCompany('');
    setFilterStatus('');
    setSelectedSites([]);
    refetch();
  };

  // ตัวแปรสำหรับตรวจสอบ Active Filters
  const hasActiveFilters = filterCompany || filterStatus || searchTerm;

  // ส่วน Action Buttons และ Search & Filter Card - Enhanced Design
  const renderSearchAndFilterSection = () => {
    return (
      <div className="mb-5 space-y-4">
        {/* Action Buttons Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <CanCreate module="sites">
              <button
                onClick={handleAddSite}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-primary-600 text-white text-sm font-medium rounded-lg shadow-button hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                <RiAddLine className="w-4 h-4 mr-2" />
                เพิ่มโครงการ
              </button>
            </CanCreate>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-lg shadow-button hover:bg-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            >
              <RiRefreshLine className="w-4 h-4 mr-2" />
              รีเฟรช
            </button>
          </div>

          {canExport('sites') && (
            <button
              onClick={handleExportExcel}
              disabled={sites.length === 0}
              className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-lg shadow-button hover:bg-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiFileDownloadLine className="w-5 h-5 mr-2 text-success-600" />
              ส่งออก Excel
            </button>
          )}
        </div>

        {/* Search & Filter Card */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 mb-4 overflow-hidden">
          {/* Card Header */}
          <div className="px-4 py-3 bg-secondary-50 border-b border-secondary-200">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
                <RiFilterLine className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-semibold text-secondary-800">ค้นหาและกรองข้อมูล</h2>
              {hasActiveFilters && (
                <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  กำลังกรอง
                </span>
              )}
            </div>
          </div>

          {/* Card Body */}
          <div className="px-4 py-3">
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <RiSearchLine className="w-5 h-5 text-secondary-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="ค้นหาชื่อโครงการ, รหัสโครงการ, รหัส Activate, รหัสลูกค้า..."
                  className="input pl-11 pr-11 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    aria-label="ล้างคำค้นหา"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-secondary-400 hover:text-secondary-600 transition-colors"
                  >
                    <RiCloseLine className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-end gap-4">
              {/* Company Filter */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-secondary-600 mb-1.5">
                  บริษัท
                </label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'ทั้งหมด' },
                    ...companies.map(company => ({
                      value: company.id,
                      label: company.name
                    }))
                  ]}
                  value={filterCompany}
                  onChange={(value) => {
                    setFilterCompany(value.toString());
                    setCurrentPage(1);
                  }}
                  placeholder="เลือกบริษัท"
                  searchPlaceholder="ค้นหาบริษัท..."
                />
              </div>

              {/* Status Filter */}
              <div className="min-w-[160px]">
                <label className="block text-sm font-medium text-secondary-600 mb-1.5">
                  สถานะ
                </label>
                <select
                  aria-label="กรองตามสถานะ"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="select text-sm"
                >
                  <option value="">ทั้งหมด</option>
                  <option value="active">ใช้งาน</option>
                  <option value="inactive">ไม่ใช้งาน</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={() => {
                    setFilterCompany('');
                    setFilterStatus('');
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="inline-flex items-center h-[42px] px-4 text-sm font-medium text-danger-700 bg-danger-50 hover:bg-danger-100 rounded-lg border border-danger-200 transition-colors"
                >
                  <RiFilterOffLine className="w-4 h-4 mr-1.5" />
                  ล้างตัวกรอง
                </button>
              )}
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-secondary-100">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-secondary-600">ตัวกรองที่ใช้:</span>
                  {searchTerm && (
                    <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 bg-secondary-100 text-secondary-700 rounded-lg text-xs font-medium group hover:bg-secondary-200 transition-colors">
                      <RiSearchLine className="w-3 h-3 mr-1.5 text-secondary-500" />
                      "{searchTerm}"
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        aria-label={`ลบตัวกรองคำค้นหา: ${searchTerm}`}
                        className="ml-2 text-secondary-400 hover:text-secondary-600 transition-colors"
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                  {filterCompany && (
                    <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 bg-primary-100 text-primary-700 rounded-lg text-xs font-medium group hover:bg-primary-200 transition-colors">
                      <RiBuilding2Line className="w-3 h-3 mr-1.5" />
                      {companies.find(c => c.id === Number(filterCompany))?.name || 'บริษัท'}
                      <button
                        onClick={() => {
                          setFilterCompany('');
                          setCurrentPage(1);
                        }}
                        aria-label={`ลบตัวกรองบริษัท: ${companies.find(c => c.id === Number(filterCompany))?.name || 'บริษัท'}`}
                        className="ml-2 text-primary-400 hover:text-primary-600 transition-colors"
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                  {filterStatus && (
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filterStatus === 'active'
                        ? 'bg-success-100 text-success-700 hover:bg-success-200'
                        : 'bg-danger-100 text-danger-700 hover:bg-danger-200'
                    }`}>
                      {filterStatus === 'active' ? (
                        <RiCheckLine className="w-3 h-3 mr-1.5" />
                      ) : (
                        <RiCloseCircleLine className="w-3 h-3 mr-1.5" />
                      )}
                      {filterStatus === 'active' ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                      <button
                        onClick={() => {
                          setFilterStatus('');
                          setCurrentPage(1);
                        }}
                        aria-label={`ลบตัวกรองสถานะ: ${filterStatus === 'active' ? 'ใช้งาน' : 'ไม่ใช้งาน'}`}
                        className={`ml-2 transition-colors ${
                          filterStatus === 'active'
                            ? 'text-success-400 hover:text-success-600'
                            : 'text-danger-400 hover:text-danger-600'
                        }`}
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ส่วน Bulk Actions Bar - Enhanced Design
  const renderBulkActionsBar = () => {
    if (selectedSites.length === 0 || !canDelete('sites')) return null;

    return (
      <div className="mb-4 bg-primary-50 border border-primary-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
              <input
                type="checkbox"
                checked={true}
                onChange={() => setSelectedSites([])}
                className="w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
            </div>
            <div>
              <span className="text-sm font-semibold text-primary-900">
                เลือกแล้ว {selectedSites.length} รายการ
              </span>
              <p className="text-xs text-primary-600">คลิกเพื่อยกเลิกการเลือกทั้งหมด</p>
            </div>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={bulkLoading}
            className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 rounded-lg shadow-button transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiDeleteBin2Line className="w-4 h-4 mr-2" />
            {bulkLoading ? 'กำลังลบ...' : 'ลบที่เลือก'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="w-full">
        {/* Page Header */}
        {renderPageHeader()}

        <ListLoadErrorBanner error={sitesError} entityLabel="โครงการ" onRetry={() => refetch()} />

        {/* Search & Filter Section */}
        {renderSearchAndFilterSection()}

        {/* Bulk Actions Bar - แสดงเฉพาะตอนเลือกข้อมูล */}
        {renderBulkActionsBar()}

        {/* Table Container with Enhanced Styling */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 overflow-hidden">
          <Table
            columns={columns}
            data={sites}
            loading={loading}
            pagination={false}
            searchable={false}
            sortable={true}
            actions={false}
          />
        </div>

        {/* Pagination */}
        {!loading && sites.length > 0 && (
          <div className="mt-5">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              showSummary={true}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && !sitesError && sites.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-12 text-center">
            <div className="w-20 h-20 mx-auto bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
              <RiMapPinLine className="w-10 h-10 text-secondary-400" />
            </div>
            <h3 className="text-lg font-semibold text-secondary-800 mb-2">ไม่พบข้อมูลโครงการ</h3>
            <p className="text-secondary-500 mb-6 max-w-md mx-auto">
              {hasActiveFilters
                ? 'ไม่พบโครงการที่ตรงกับเงื่อนไขการค้นหา ลองปรับตัวกรองหรือล้างตัวกรองเพื่อดูข้อมูลทั้งหมด'
                : 'ยังไม่มีข้อมูลโครงการในระบบ เริ่มต้นโดยการเพิ่มโครงการใหม่'}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={() => {
                  setFilterCompany('');
                  setFilterStatus('');
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg shadow-button transition-colors"
              >
                <RiFilterOffLine className="w-4 h-4 mr-2" />
                ล้างตัวกรอง
              </button>
            ) : canCreate('sites') ? (
              <button
                onClick={handleAddSite}
                className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-button transition-colors"
              >
                <RiAddLine className="w-4 h-4 mr-2" />
                เพิ่มโครงการแรก
              </button>
            ) : null}
          </div>
        )}

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default SitePage;
